// src/pages/dashboards/user/UserSubscription.jsx
// Plan comparison table + a manual-EFT upgrade flow. Banking details come from
// the API; "I've paid" flags the payment for recon. Discount codes apply in the
// upgrade modal (percentage/fixed reduce the amount due). Prepaid scan packs
// sit below the plans - buy extra scans that never expire.

import React, { useEffect, useState } from "react";
import { getSubscription, getPlans, upgradeIntent, getBankingDetails, voucherPreview, getPacks, packIntent } from "../../../api/user";
import { Card, Button, Badge, Loading, Input, Modal } from "../../../components/ui";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import { money, date, titleCase } from "../../../lib/format";

const FEATURE_LABEL = {
  exportPack: "SARS export pack",
  webManage: "Web portal access",
  splits: "Receipt splitting",
  practitioner: "Practitioner access",
};
const FEATURE_KEYS = Object.keys(FEATURE_LABEL);
const ORDER = ["free", "starter", "pro"];
const TIER_RANK = { free: 0, starter: 1, pro: 2 };

export default function UserSubscription() {
  const toast = useToast();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [upgrade, setUpgrade] = useState(null);
  const [packs, setPacks] = useState([]);
  const [buyPack, setBuyPack] = useState(null);

  useEffect(() => {
    getSubscription().then(setSub).catch((e) => toast.error(e.message));
    getPlans().then(setPlans).catch(() => {});
    getPacks().then(setPacks).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  if (!sub) return <Loading label="Loading your plan" />;

  const currentKey = sub.tier?.key || sub.subscription?.tier || "free";
  const currentRank = TIER_RANK[currentKey] ?? 0;
  const byKey = Object.fromEntries(plans.map((p) => [p.key, p]));
  const cols = ORDER.map((k) => byKey[k]).filter(Boolean);
  const scanText = (scan) => (scan?.limit == null ? "Unlimited" : `${scan.limit}`);

  return (
    <div>
      {/* slim current-plan line */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Plan</h1>
          <Badge tone="green">On {sub.tier?.label || titleCase(currentKey)}</Badge>
        </div>
        {sub.subscription?.expires_at && (
          <span className="text-xs text-tb-muted">Renews / ends {date(sub.subscription.expires_at)}</span>
        )}
      </div>

      {/* mobile: stacked plan cards */}
      <div className="md:hidden space-y-4">
        {cols.map((p) => {
          const cur = p.key === currentKey;
          const isUp = (TIER_RANK[p.key] ?? 0) > currentRank;
          return (
            <Card key={p.key} className={`p-5 ${cur ? "ring-2 ring-tb-brand" : ""}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-lg">{p.label}</span>
                {cur ? <Badge tone="green">Current</Badge> : p.key === "pro" ? <Badge tone="amber">Popular</Badge> : null}
              </div>
              <div className="text-2xl font-bold">
                {p.price ? money(p.price) : "Free"}
                {p.price ? <span className="text-sm font-normal text-tb-muted"> /yr</span> : null}
              </div>
              <p className="mt-1 text-xs text-tb-muted">
                {p.scan?.limit == null ? "Unlimited scans" : `${p.scan.limit} scans / year`}
              </p>

              <div className="mt-4 space-y-2">
                {FEATURE_KEYS.map((k) => (
                  <div key={k} className="flex items-center gap-2 text-sm">
                    {p.features?.[k]
                      ? <span className="text-tb-brand font-semibold">{"\u2713"}</span>
                      : <span className="text-tb-muted">{"\u2013"}</span>}
                    <span className={p.features?.[k] ? "" : "text-tb-muted"}>{FEATURE_LABEL[k]}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5">
                {cur ? <Button variant="subtle" className="w-full" disabled>Current plan</Button>
                  : isUp ? <Button className="w-full" onClick={() => setUpgrade(p)}>Upgrade</Button>
                  : <Button variant="ghost" className="w-full" disabled>Included</Button>}
              </div>
            </Card>
          );
        })}
      </div>

      {/* desktop: comparison table */}
      <Card className="hidden md:block overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="p-5 text-left align-bottom w-[34%]">
                  <span className="text-xs uppercase tracking-wider text-tb-muted">Compare plans</span>
                </th>
                {cols.map((p) => {
                  const cur = p.key === currentKey;
                  return (
                    <th key={p.key}
                        className={`p-5 text-center align-bottom border-l border-tb-border dark:border-tb-d-border ${cur ? "bg-tb-wash dark:bg-tb-brand/10" : ""}`}>
                      <div className="flex items-center justify-center gap-2">
                        <span className="font-bold text-base">{p.label}</span>
                        {cur ? <Badge tone="green">Current</Badge> : p.key === "pro" ? <Badge tone="amber">Popular</Badge> : null}
                      </div>
                      <div className="mt-1 text-xl font-bold">
                        {p.price ? money(p.price) : "Free"}
                        {p.price ? <span className="text-xs font-normal text-tb-muted"> /yr</span> : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <Trow label="Scans per year" cols={cols} currentKey={currentKey}
                    render={(p) => <span className="font-medium">{scanText(p.scan)}</span>} />
              {FEATURE_KEYS.map((k) => (
                <Trow key={k} label={FEATURE_LABEL[k]} cols={cols} currentKey={currentKey}
                      render={(p) => p.features?.[k]
                        ? <span className="text-tb-brand font-semibold">{"\u2713"}</span>
                        : <span className="text-tb-muted">{"\u2013"}</span>} />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="p-4"></td>
                {cols.map((p) => {
                  const cur = p.key === currentKey;
                  const isUpgrade = (TIER_RANK[p.key] ?? 0) > currentRank;
                  return (
                    <td key={p.key} className={`p-4 border-l border-tb-border dark:border-tb-d-border ${cur ? "bg-tb-wash dark:bg-tb-brand/10" : ""}`}>
                      {cur ? (
                        <Button variant="subtle" className="w-full" disabled>Current</Button>
                      ) : isUpgrade ? (
                        <Button className="w-full" onClick={() => setUpgrade(p)}>Upgrade</Button>
                      ) : (
                        <span className="block text-center text-xs text-tb-muted">Included</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* scan top-up packs */}
      {packs.length > 0 && (
        <div className="mt-10">
          <div className="mb-4">
            <h2 className="text-xl font-bold">Top-up scan packs</h2>
            <p className="text-sm text-tb-muted mt-1">
              Run out before renewal? Buy a prepaid pack - credits never expire and stack on top of your plan.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {packs.map((p) => (
              <Card key={p.key} className="p-5 text-center">
                <p className="text-3xl font-bold text-tb-brand">{p.scans}</p>
                <p className="text-sm text-tb-muted mt-1">scans</p>
                <p className="text-xl font-bold mt-3">{money(p.price)}</p>
                <p className="text-xs text-tb-muted">{money(p.perScan)} per scan</p>
                <Button className="w-full mt-4" onClick={() => setBuyPack(p)}>Buy pack</Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {upgrade && <UpgradeModal plan={upgrade} onClose={() => setUpgrade(null)} />}
      {buyPack && <PackModal pack={buyPack} onClose={() => setBuyPack(null)} />}
    </div>
  );
}

function Trow({ label, cols, currentKey, render }) {
  return (
    <tr className="border-t border-tb-border dark:border-tb-d-border">
      <td className="px-5 py-3 text-tb-muted">{label}</td>
      {cols.map((p) => (
        <td key={p.key}
            className={`px-5 py-3 text-center border-l border-tb-border dark:border-tb-d-border ${p.key === currentKey ? "bg-tb-wash dark:bg-tb-brand/10" : ""}`}>
          {render(p)}
        </td>
      ))}
    </tr>
  );
}

function Row({ label, value }) {
  const toast = useToast();
  const copy = () => navigator.clipboard?.writeText(String(value)).then(() => toast.info?.("Copied"), () => {});
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
      <span className="text-tb-muted shrink-0">{label}</span>
      <button onClick={copy} className="font-medium hover:text-tb-brand transition-colors truncate text-right" title="Copy">{value}</button>
    </div>
  );
}

function UpgradeModal({ plan, onClose }) {
  const toast = useToast();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [bank, setBank] = useState(null);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [checking, setChecking] = useState(false);
  const reference = (user?.email || "your email").toLowerCase();

  useEffect(() => { getBankingDetails().then(setBank).catch(() => setBank({})); }, []);

  const net = (() => {
    if (!discount) return plan.price;
    if (discount.discount_type === "percentage") return Math.max(0, Math.round(plan.price * (1 - discount.discount_value / 100)));
    if (discount.discount_type === "fixed") return Math.max(0, plan.price - discount.discount_value);
    return plan.price;
  })();

  const applyCode = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const d = await voucherPreview(code.trim());
      if (d.discount_type === "free_months") {
        toast.info?.("That code can't be applied to a payment.");
        setDiscount(null);
      } else {
        setDiscount({ ...d, code: code.trim() });
        toast.success("Discount applied");
      }
    } catch (e) { toast.error(e.message); setDiscount(null); } finally { setChecking(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await upgradeIntent(plan.key, discount?.code);
      toast.success("Thanks — we'll watch for your payment and activate it once it clears");
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const discountLabel = discount
    ? (discount.discount_type === "percentage" ? `${discount.discount_value}% off` : `${money(discount.discount_value)} off`)
    : null;

  return (
    <Modal open onClose={onClose} title={`Upgrade to ${plan.label}`} width="max-w-2xl" bodyClassName="p-6">
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* left: amount + discount */}
          <div className="space-y-4">
            <div className="rounded-2xl p-5 bg-tb-wash dark:bg-tb-brand/10 border border-tb-brand/20 text-center">
              <p className="text-xs uppercase tracking-wider text-tb-brand/80">Amount due</p>
              {discount ? (
                <div className="mt-1 flex items-baseline justify-center gap-2">
                  <span className="text-lg text-tb-muted line-through">{money(plan.price)}</span>
                  <span className="text-4xl font-bold text-tb-brand leading-none">{money(net)}</span>
                </div>
              ) : (
                <p className="text-4xl font-bold text-tb-brand leading-none mt-1">{money(plan.price)}</p>
              )}
              <p className="mt-2 text-xs text-tb-muted">
                per year · once-off EFT{discountLabel ? ` · ${discountLabel} (${discount.code})` : ""}
              </p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Discount code</p>
              <div className="flex gap-2">
                <Input placeholder="Optional" value={code}
                       onChange={(e) => { setCode(e.target.value.toUpperCase()); setDiscount(null); }} />
                <Button variant="subtle" onClick={applyCode} disabled={checking || !code.trim()}>Apply</Button>
              </div>
            </div>
          </div>

          {/* right: EFT details */}
          <div>
            <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Pay by EFT to</p>
            {!bank ? <Loading label="Loading details" /> : (
              <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
                <Row label="Account name" value={bank.accountName} />
                <Row label="Bank" value={bank.bank} />
                <Row label="Account number" value={bank.accountNumber} />
                <Row label="Branch code" value={bank.branchCode} />
                <Row label="Account type" value={bank.accountType} />
                <Row label="Amount" value={money(net)} />
                <Row label="Reference" value={reference} />
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-3 text-xs text-tb-muted space-y-1.5">
          <p>Pay the exact amount and use your email as the reference so we can match it. Tap any value to copy.</p>
          <p>Once you've made the EFT, tap "I've paid" — we'll activate {plan.label} once it reflects, usually within 1–2 business days.</p>
          {bank?.payEmail && <p>Need help? {bank.payEmail}</p>}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={markPaid} disabled={busy}>I've paid</Button>
        </div>
      </div>
    </Modal>
  );
}

function PackModal({ pack, onClose }) {
  const toast = useToast();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [bank, setBank] = useState(null);
  const reference = `${(user?.email || "your email").toLowerCase()}-pack`;

  useEffect(() => { getBankingDetails().then(setBank).catch(() => setBank({})); }, []);

  const markPaid = async () => {
    setBusy(true);
    try {
      await packIntent(pack.key);
      toast.success("Thanks — we'll add your scans once the payment clears");
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Buy ${pack.label}`} width="max-w-lg" bodyClassName="p-6">
      <div className="space-y-5">
        <div className="rounded-2xl p-5 bg-tb-wash dark:bg-tb-brand/10 border border-tb-brand/20 text-center">
          <p className="text-xs uppercase tracking-wider text-tb-brand/80">Top-up pack</p>
          <p className="text-4xl font-bold text-tb-brand leading-none mt-1">{pack.scans} scans</p>
          <p className="mt-2 text-lg font-bold">{money(pack.price)}</p>
          <p className="text-xs text-tb-muted mt-1">once-off EFT · credits never expire</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Pay by EFT to</p>
          {!bank ? <Loading label="Loading details" /> : (
            <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
              <Row label="Account name" value={bank.accountName} />
              <Row label="Bank" value={bank.bank} />
              <Row label="Account number" value={bank.accountNumber} />
              <Row label="Branch code" value={bank.branchCode} />
              <Row label="Account type" value={bank.accountType} />
              <Row label="Amount" value={money(pack.price)} />
              <Row label="Reference" value={reference} />
            </div>
          )}
        </div>

        <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-3 text-xs text-tb-muted space-y-1.5">
          <p>Pay the exact amount and use the reference above so we can match it. Tap any value to copy.</p>
          <p>Once you've made the EFT, tap "I've paid" — we'll add {pack.scans} scans to your account once it reflects, usually within 1–2 business days.</p>
          {bank?.payEmail && <p>Need help? {bank.payEmail}</p>}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={markPaid} disabled={busy}>I've paid</Button>
        </div>
      </div>
    </Modal>
  );
}